import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EmployeeService } from 'src/app/modules/employee/services/employee.service';
import { DepartmentService } from 'src/app/modules/organization/services/department.service';
import { DemoService } from 'src/app/modules/auth/services/demo/demo.service';
import { CompanyService } from 'src/app/modules/settings/services/company.service';
import { SubscriptionService } from 'src/app/modules/feature-toggle/services/subscription.service';
import {
  getIconFor,
  getShiftPlannerProfileFor,
} from 'src/app/core/data/company-type/company-type-registry';
import { Feature } from 'src/app/modules/feature-toggle/enums/features.enum';
import { XdsButtonComponent } from 'src/app/shared/ui/button/button.component';
import { XdsChipComponent } from 'src/app/shared/ui/chip/chip.component';
import { XdsActionItemDirective } from 'src/app/shared/ui/action-item/action-item.directive';
import { XdsSkeletonComponent } from 'src/app/shared/ui/skeleton/skeleton.component';
import { demoSuggestions } from 'src/app/help/config/demo-suggestions';
import {
  ASSISTANT_TOPICS,
  AssistantTopic,
  AssistantTopicId,
  CAPABILITIES_QUESTION_KEY,
} from 'src/app/help/config/assistant-topics';
import { profileSuggestions } from 'src/app/help/config/profile-suggestions';
import { CompanyPulseService, PulseKind } from 'src/app/help/services/company-pulse.service';

interface SuggestionGroups {
  /** „Erfahren" — informierende Beispiele (Frage). */
  ask: string[];
  /** „Erledigen" — handelnde Beispiele (Aktion). */
  do: string[];
  /** true = leeres Konto (nur Einrichtungs-Vorschläge). */
  setup: boolean;
  /**
   * true = es gibt mehr Beispiele als angezeigte, „Andere Beispiele" bewirkt also etwas.
   *
   * Bewusst getrennt von `setup`: Auch die feste Demo-Liste ist nicht rotierbar, ist aber kein
   * leeres Konto. Ein Knopf, der nichts tut, ist schlimmer als keiner.
   */
  rotatable: boolean;
}

/** Eine Zeile in „Heute in deinem Betrieb" — fertig übersetzt und mit Folgefrage. */
interface PulseLine {
  count: number;
  text: string;
  question: string;
}

/**
 * Empty-State des Assistenten.
 *
 * ## Was hier seit dem 09.09.2026 anders ist
 *
 * **C5 · „Heute in deinem Betrieb"** steht ganz oben — drei antippbare Zeilen aus dem
 * ECHTEN Zustand des Betriebs. Das ist der Unterschied zwischen „Wobei kann ich helfen?"
 * und „Hier ist, was ansteht". Quelle: {@link CompanyPulseService} (vier fertige
 * REST-Endpunkte, **keine** KI-Anfrage).
 *
 * **C5 · Passend zu …** macht sichtbar, worauf sich die Beispiele beziehen (Branche, Größe,
 * Struktur). Ohne diese Zeile wirken auch gute Vorschläge beliebig — mit ihr wirken sie
 * begründet.
 *
 * **C5 · Gefiltert und in der Sprache der Branche.** Ein Pflegedienst mit 24 Personen und
 * ein Lieferbetrieb mit 200 bekamen bis dahin dieselben sechs Sätze. Jetzt entscheidet das
 * `shiftPlannerProfile` (aus `CompanyTypeId`) die Wortwahl, und Beispiele, für die es die
 * Voraussetzung nicht gibt, fallen weg — keine Abteilungsvergleiche bei einer Abteilung.
 *
 * **C1 · Sechs Themen statt sechs Sätze.** 82 Werkzeuge lassen sich nicht auflisten; sechs
 * Themen sind die Landkarte. Darunter „Alles ansehen, was ich kann" → `get_capabilities`,
 * womit der Assistent die Frage nach seinem Umfang selbst beantwortet und automatisch
 * aktuell bleibt.
 *
 * ---
 *
 * **WICHTIG (Daten-Sicherheit, unverändert):** Beispiele mit Personen-/Abteilungsbezug
 * werden mit ECHTEN, im Tenant vorhandenen Namen befüllt (EmployeeService/DepartmentService)
 * — niemals mit Platzhaltern wie „Tom". Sonst antwortet der Bot „… existiert nicht" und
 * verwirrt. Gibt es keine Mitarbeiter (frisches Konto), erscheinen ausschließlich
 * entitätsfreie Einrichtungs-Vorschläge.
 */
@Component({
  selector: 'app-help-suggested-questions',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    XdsButtonComponent,
    XdsChipComponent,
    XdsSkeletonComponent,
    // ⚠️ Attribut-Direktive: ohne Eintrag hier bleibt sie WIRKUNGSLOS — kein Fehler,
    // keine Warnung, nur eine Zeile ohne Kit-Optik und ohne Trefferziel.
    XdsActionItemDirective,
  ],
  template: `
    <div class="suggested" role="group" data-testid="help-suggested-questions" [attr.aria-label]="'HELP_CHAT.SUGGESTED.LABEL' | translate">
      <h2 class="suggested__title">{{ 'HELP_CHAT.TITLE' | translate }}</h2>

      <!-- C5 Schritt 1 — der echte Zustand, ganz oben. Solange geladen wird: Skelett,
           nie ein Blockieren des Öffnens. Nichts offen -> Karte entfällt ersatzlos. -->
      <section class="xds-card pulse" *ngIf="pulseVisible()">
        <header class="xds-card__header">
          <div class="icon"><i class="fa-solid fa-bell" aria-hidden="true"></i></div>
          <h3 class="xds-card__title">{{ 'HELP_CHAT.TODAY.TITLE' | translate }}</h3>
        </header>
        <div class="xds-card__body">
          <ng-container *ngIf="pulseLines() as lines; else pulseSkeleton">
            <!-- Kit-Baustein statt Eigenbau: [xdsActionItem] bringt die Zeilen-Optik UND
                 das 44px-Trefferziel auf Touch zentral mit (echte min-height, kein
                 Ausleger — dicht gestapelte Zeilen wuerden sich sonst die Klicks
                 abfangen). Eine eigene Knopf-Zeile waere die zweite Bauform fuer
                 dieselbe Sache. -->
            <button
              xdsActionItem
              type="button"
              class="pulse__row"
              *ngFor="let line of lines"
              (click)="select(line.question)"
            >
              <span class="pulse__num">{{ line.count }}</span>
              <span class="pulse__text">{{ line.text }}</span>
              <i class="fa-light fa-chevron-right pulse__go" aria-hidden="true"></i>
            </button>
          </ng-container>
          <ng-template #pulseSkeleton>
            <xds-skeleton variant="line"></xds-skeleton>
            <xds-skeleton variant="line" width="80%"></xds-skeleton>
          </ng-template>
        </div>
      </section>

      <!-- C5 Schritt 2+3 — worauf sich die Beispiele beziehen. -->
      <div class="scope" *ngIf="scopeChips().length">
        <span class="scope__label">{{ 'HELP_CHAT.SCOPE_LABEL' | translate }}</span>
        <xds-chip
          *ngFor="let chip of scopeChips()"
          [label]="chip.label"
          [icon]="chip.icon"
          [compact]="true"
          [interactive]="false"
        ></xds-chip>
      </div>

      <p class="suggested__hint">{{ 'HELP_CHAT.SUGGESTED.HINT' | translate }}</p>

      <!-- C1 — die Landkarte. Themen sind Filter, kein Absenden: Wer „Zeiten" antippt,
           sieht die Beispiele dieses Themas und entscheidet dann. -->
      <div class="topics" *ngIf="visibleTopics().length > 1">
        <span class="topics__label">{{ 'HELP_CHAT.TOPICS_LABEL' | translate }}</span>
        <div class="xds-chip-group">
          <xds-chip
            *ngFor="let topic of visibleTopics()"
            [labelKey]="topic.labelKey"
            [icon]="topic.icon"
            [active]="activeTopic() === topic.id"
            (toggled)="toggleTopic(topic.id)"
          ></xds-chip>
        </div>
      </div>

      <!-- Erfahren (Frage) -->
      <div class="grp grp--ask" *ngIf="groups().ask.length">
        <div class="grp__head">
          <i class="fa-light fa-circle-info grp__icon" aria-hidden="true"></i>
          <span class="grp__label">{{ 'HELP_CHAT.SUGGESTED.GROUP_ASK' | translate }}</span>
        </div>
        <div class="grp__list">
          <xds-button
            *ngFor="let s of groups().ask"
            variant="secondary"
            [block]="true"
            layout="spread"
            [revealTrailingIcon]="true"
            iconRight="fa-light fa-arrow-right"
            (clicked)="select(s)"
          >
            {{ s }}
          </xds-button>
        </div>
      </div>

      <!-- Erledigen (Aktion) -->
      <div class="grp grp--do" *ngIf="groups().do.length">
        <div class="grp__head">
          <i class="fa-light fa-bolt grp__icon" aria-hidden="true"></i>
          <span class="grp__label">{{ 'HELP_CHAT.SUGGESTED.GROUP_DO' | translate }}</span>
          <xds-button
            *ngIf="groups().rotatable"
            variant="ghost"
            size="sm"
            class="grp__refresh"
            iconLeft="fa-light fa-arrows-rotate"
            (clicked)="refresh()"
          >
            {{ 'HELP_CHAT.SUGGESTED.REFRESH' | translate }}
          </xds-button>
        </div>
        <div class="grp__list">
          <xds-button
            *ngFor="let s of groups().do"
            variant="secondary"
            [block]="true"
            layout="spread"
            [revealTrailingIcon]="true"
            iconRight="fa-light fa-arrow-right"
            (clicked)="select(s)"
          >
            {{ s }}
          </xds-button>
        </div>
      </div>

      <!-- C1 — der Weg zu den restlichen Werkzeugen. Diese Liste ist die Abkürzung,
           get_capabilities ist die Wahrheit. -->
      <xds-button
        *ngIf="!groups().setup"
        variant="ghost"
        [block]="true"
        iconLeft="fa-light fa-list"
        (clicked)="askCapabilities()"
      >
        {{ 'HELP_CHAT.SHOW_CAPABILITIES' | translate }}
      </xds-button>
    </div>
  `,
  styles: [
    `
      /* A4 — Abstände und Schriftgrößen kommen aus Tokens statt aus rohen px.
         --sp-* und --fs-* haben KEINE Gerätestufe: wer am Telefon kleiner will,
         nimmt ein anderes vorhandenes Token, nie eine Zahl. */
      .suggested {
        display: flex;
        flex-direction: column;
        gap: var(--sp-4);
        padding: var(--sp-2) var(--sp-4) var(--sp-5);
      }
      .suggested__title {
        margin: 0;
        font-size: var(--fs-lg);
        font-weight: 700;
        color: var(--xds-text);
      }
      .suggested__hint {
        margin: 0;
        font-size: var(--fs-sm);
        color: var(--text-muted);
        line-height: 1.4;
      }

      /* „Heute in deinem Betrieb" — Karte aus dem Kit, Zeilen sind eigener Inhalt.
         Bewusst KEINE Warnfarben und kein Zähler am Launcher: Der Assistent ist ein
         Helfer, kein Mahnsystem. */
      /* NUR aeusseres Layout — Flaeche, Hoehe, Hover und Trefferziel kommen von
         [xdsActionItem]. Ein Selektor, der hier .xds-action-item umstylte, waere ein
         XDS-Uebergriff (SELECTOR_TARGETS_XDS); die Baseline steht auf 0. */
      .pulse__row {
        gap: var(--sp-3);
        width: 100%;
      }
      .pulse__num {
        flex: none;
        min-width: 22px;
        height: 22px;
        display: grid;
        place-items: center;
        border-radius: var(--r-sm, 6px);
        background: var(--tint-primary-bg);
        color: var(--tint-primary-text);
        font-size: var(--fs-xs);
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
      .pulse__text {
        flex: 1;
        min-width: 0;
      }
      .pulse__go {
        flex: none;
        color: var(--text-subtle);
        font-size: var(--fs-xs);
      }

      /* „Passend zu Pflege · 24 Mitarbeiter · 4 Abteilungen" */
      .scope {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--sp-2);
      }
      .scope__label {
        font-size: var(--fs-xs);
        color: var(--text-muted);
      }

      .topics {
        display: flex;
        flex-direction: column;
        gap: var(--sp-2);
      }
      .topics__label {
        font-size: var(--fs-xs);
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--text-muted);
      }

      .grp {
        display: flex;
        flex-direction: column;
        gap: var(--sp-2);
      }
      .grp__head {
        display: flex;
        align-items: center;
        gap: var(--sp-2);
      }
      .grp__icon {
        font-size: var(--fs-base);
      }
      .grp--ask .grp__icon {
        /* WARUM: --sug-ask/--sug-do waren nie definierte Tokens (dangling var(), fiel still
           auf die geerbte Textfarbe zurueck) — echte Token-Rollen machen die zwei Kategorien
           jetzt tatsaechlich sichtbar unterscheidbar. */
        color: var(--action-primary);
      }
      .grp--do .grp__icon {
        color: var(--warning);
      }
      .grp__label {
        font-size: var(--fs-xs);
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--text-muted);
      }
      .grp__refresh {
        margin-left: auto;
      }
      .grp__list {
        display: flex;
        flex-direction: column;
        gap: var(--sp-2);
      }
    `,
  ],
})
export class HelpSuggestedQuestionsComponent implements OnInit {
  /** Optionaler zusaetzlicher Tour-Kontext-Vorschlag (F-8). */
  @Input() tourContextKey: string | null = null;

  /** Emit: der angetippte Beispielsatz (Parent befüllt damit das Eingabefeld vor). */
  @Output() chipSelected = new EventEmitter<string>();

  private readonly translate = inject(TranslateService);
  private readonly employeeService = inject(EmployeeService);
  private readonly departmentService = inject(DepartmentService);
  private readonly demoService = inject(DemoService);
  private readonly companyService = inject(CompanyService);
  private readonly subscription = inject(SubscriptionService);
  private readonly pulse = inject(CompanyPulseService);
  private readonly destroyRef = inject(DestroyRef);

  /** Anzeigenamen echter, im Tenant vorhandener Mitarbeiter/Abteilungen. */
  private readonly employees = signal<string[]>([]);
  private readonly departments = signal<string[]>([]);
  /** Firmenart (Registry-Key) — treibt die Branchensprache der Beispiele. */
  private readonly companyType = signal<string | null>(null);
  /** Rotations-Anker für „Andere Beispiele" (wechselt Vorschläge + Namen). */
  private readonly seed = signal(0);
  /** Tickt bei Sprachwechsel, damit die Beispiele neu uebersetzt werden. */
  private readonly langTick = signal(0);
  /** C1: gewähltes Thema — `null` = die gemischte Standardauswahl wie bisher. */
  private readonly activeTopicId = signal<AssistantTopicId | null>(null);

  // Entitätsfreie Beispiele funktionieren in JEDEM Konto.
  private static readonly ASK_FREE = [
    'ASK_WHO_WORKS',
    'ASK_COVERAGE',
    'ASK_LIVE',
    'ASK_BRIEFING',
    'ASK_CLOCK',
  ];
  // Entitätsgebundene Beispiele nur, wenn echte Mitarbeiter existieren.
  private static readonly ASK_EMP = ['ASK_VACATION', 'ASK_EMP_SHIFTS'];
  private static readonly DO_FREE = ['DO_AUTOFILL', 'DO_PUBLISH', 'DO_ADD_EMP'];
  private static readonly DO_EMP = ['DO_PLAN', 'DO_SICK', 'DO_VACATION'];
  // Frisches Konto (keine Mitarbeiter): nur Einrichtung, ohne Namen.
  private static readonly SETUP_ASK = ['SETUP_HELP'];
  private static readonly SETUP_DO = ['SETUP_FIRST_EMP', 'SETUP_DEPT'];

  /**
   * C5 Schritt 2 — Beispiele, die eine Voraussetzung haben.
   *
   * Ein Vorschlag, der im eigenen Betrieb gar nicht beantwortbar ist, ist schlimmer als
   * keiner: „Wer hat vergessen auszustempeln?" ohne Zeiterfassung führt zu einer
   * Verlegenheitsantwort und kostet in einer Demo eine von zehn KI-Anfragen.
   */
  private static readonly NEEDS_DEPARTMENT = new Set(['ASK_COVERAGE']);
  private static readonly NEEDS_TIME_TRACKING = new Set(['ASK_LIVE', 'ASK_CLOCK']);

  readonly groups = computed<SuggestionGroups>(() => this.buildGroups());
  readonly activeTopic = computed(() => this.activeTopicId());

  /**
   * Themen, die dieser Betrieb tatsächlich benutzen kann (Feature-Gate).
   *
   * ⚠️ In der Demo KEINE Themen. Nicht aus Sparsamkeit, sondern weil dort schon ein
   * Entdeckungsangebot steht: die kuratierten Branchensätze. Zwei nebeneinander
   * konkurrieren um dieselbe Aufmerksamkeit — und am Gerät gemessen (390×844) schob der
   * Themenblock den leeren Zustand über die Bildschirmhöhe (781 statt 716 px), sodass
   * ausgerechnet die Branchensätze unter den Rand rutschten. Der Besucher hat im Mittel
   * 102 Sekunden; die Landkarte über 82 Werkzeuge ist die Aufgabe des Alltags, nicht der
   * Vorführung.
   */
  readonly visibleTopics = computed<AssistantTopic[]>(() => {
    if (this.demoService.isDemoActive()) return [];
    return ASSISTANT_TOPICS.filter(
      (t) => t.requiresFeature === undefined || this.subscription.hasFeature(t.requiresFeature),
    );
  });

  /** Karte zeigen? In der Demo nicht — dort führt der Branchen-Einstieg. */
  readonly pulseVisible = computed(() => {
    if (this.demoService.isDemoActive()) return false;
    const rows = this.pulse.rows();
    // `null` = lädt noch (Skelett), `[]` = nichts offen (Karte weg).
    return rows === null || rows.length > 0;
  });

  /** Fertig übersetzte Zeilen — `null`, solange geladen wird. */
  readonly pulseLines = computed<PulseLine[] | null>(() => {
    this.langTick();
    const rows = this.pulse.rows();
    if (rows === null) return null;
    return rows.map((r) => ({
      count: r.count,
      text: this.translate.instant(this.pulseTextKey(r.kind, r.count), { count: r.count }),
      question: this.translate.instant(`HELP_CHAT.SUGGESTED.${this.pulseQuestionKey(r.kind)}`, {
        name: '',
        dept: this.departments()[0] ?? '',
      }),
    }));
  });

  /** „Passend zu …" — Branche, Größe, Struktur. Leer im frischen Konto. */
  readonly scopeChips = computed<{ label: string; icon: string }[]>(() => {
    this.langTick();
    if (this.demoService.isDemoActive()) return [];
    const emps = this.employees().length;
    const deps = this.departments().length;
    if (emps === 0) return [];

    const chips: { label: string; icon: string }[] = [];
    const typeName = this.companyType();
    if (typeName) {
      // Die Branchennamen stehen als TOP-LEVEL-Keys in de.json/en.json (der Registry-Key
      // IST der i18n-Key: `GastronomyHotel` -> „Gastronomie & Hotel"). Kein Präfix.
      const label = this.translate.instant(typeName);
      // `instant()` gibt bei unbekanntem Schlüssel den Schlüssel zurück — dann lieber
      // gar kein Chip als „GastronomyHotel" auf dem Bildschirm.
      if (label && label !== typeName) {
        chips.push({ label, icon: getIconFor(typeName) ? 'fa-solid ' + getIconFor(typeName) : 'fa-solid fa-briefcase' });
      }
    }
    chips.push({
      label: this.translate.instant(
        emps === 1 ? 'HELP_CHAT.SCOPE.EMPLOYEES_ONE' : 'HELP_CHAT.SCOPE.EMPLOYEES_MANY',
        { count: emps },
      ),
      icon: 'fa-solid fa-users',
    });
    if (deps > 0) {
      chips.push({
        label: this.translate.instant(
          deps === 1 ? 'HELP_CHAT.SCOPE.DEPARTMENTS_ONE' : 'HELP_CHAT.SCOPE.DEPARTMENTS_MANY',
          { count: deps },
        ),
        icon: 'fa-solid fa-building',
      });
    }
    return chips;
  });

  ngOnInit(): void {
    // Echte Stammdaten laden (gecacht via AppState/BehaviorSubject) und Signale füttern.
    this.employeeService.employeeNameList
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((list) => {
        const names = (list ?? [])
          .map((e) => (e?.Name ?? '').trim())
          .filter((n) => n.length > 0);
        this.employees.set(names);
      });
    this.departmentService.departementList
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((list) => {
        const names = (list ?? [])
          .map((d) => (d?.Name ?? '').trim())
          .filter((n) => n.length > 0);
        this.departments.set(names);
      });
    // C5 Schritt 3: die Firmenart. `CompanyTypeName` ist der Registry-Key
    // (GastronomyHotel, RetailGeneral …), den `getShiftPlannerProfileFor()` erwartet.
    this.companyService.companyDetails$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((d) => this.companyType.set(d?.CompanyTypeName ?? null));
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.langTick.update((t) => t + 1));

    this.employeeService.getEmployeeNameList();
    this.departmentService.getDepartementList();
    // ⚠️ Abonnieren allein reicht NICHT. `companyDetails$` startet aus dem localStorage-Cache
    // und ist ohne vorherigen Abruf `null` — am Gerät gemessen (09.09.2026): Die Firma trug
    // `CompanyTypeName: "GastronomyHotel"`, der Empty-State zeigte trotzdem die allgemeinen
    // Beispiele und keinen Branchen-Chip, weil niemand geladen hatte. Der Aufruf ist
    // idempotent (der Service hat einen eigenen In-Flight-Schutz).
    this.companyService.getCompanyDetails();
    // ⚠️ Darf das Öffnen nicht blockieren — der Service liefert bis dahin `null`
    // und die Karte zeigt ein Skelett.
    this.pulse.refresh();
  }

  protected select(sentence: string): void {
    this.chipSelected.emit(sentence);
  }

  /** „Alles ansehen, was ich kann" → wörtlich ein ExampleUtterance von get_capabilities. */
  protected askCapabilities(): void {
    this.chipSelected.emit(this.translate.instant(CAPABILITIES_QUESTION_KEY));
  }

  /** Thema an/aus — nochmals antippen zeigt wieder die gemischte Auswahl. */
  protected toggleTopic(id: AssistantTopicId): void {
    this.activeTopicId.update((cur) => (cur === id ? null : id));
  }

  protected refresh(): void {
    // Größerer Schritt -> spürbar andere Beispiele + andere Namen.
    this.seed.update((s) => s + 3);
  }

  private buildGroups(): SuggestionGroups {
    this.langTick(); // Abhaengigkeit fuer Re-Translation bei Sprachwechsel.
    const seed = this.seed();
    const hasEmployee = this.employees().length > 0;

    // In der Demo gilt eine andere Aufgabe: Der Besucher kennt das System noch nicht und hat im
    // Mittel 102 Sekunden. Statt Beispielen für die tägliche Arbeit stehen hier Sätze, die die
    // Stärken in der Sprache seiner Branche zeigen. Ohne Rotation — es gibt nur einen Anlauf.
    if (this.demoService.isDemoActive()) {
      const set = demoSuggestions(this.demoService.getIndustry());
      return {
        ask: set.ask.map((key) => this.text(key, 0)),
        do: set.do.map((key) => this.text(key, 0)),
        setup: false,
        rotatable: false,
      };
    }

    if (!hasEmployee) {
      return {
        ask: HelpSuggestedQuestionsComponent.SETUP_ASK.map((k) => this.text(k, 0)),
        do: HelpSuggestedQuestionsComponent.SETUP_DO.map((k) => this.text(k, 0)),
        setup: true,
        rotatable: false,
      };
    }

    // C1: Ein gewähltes Thema ersetzt die Mischung — es IST die Auswahl.
    const topic = this.visibleTopics().find((t) => t.id === this.activeTopicId());
    if (topic) {
      return {
        ask: this.usable(topic.askKeys).map((k, i) => this.text(k, seed + i)),
        do: this.usable(topic.doKeys).map((k, i) => this.text(k, seed + i)),
        setup: false,
        rotatable: false,
      };
    }

    // C5 Schritt 3: Hat die Branche eine eigene Sprachfarbe, führt sie.
    const profile = getShiftPlannerProfileFor(this.companyType());
    const branch = profileSuggestions(profile);
    if (branch && (!branch.needsDepartment || this.departments().length > 0)) {
      return {
        ask: this.usable(branch.ask).map((k, i) => this.text(k, seed + i)),
        do: this.usable(branch.do).map((k, i) => this.text(k, seed + i)),
        setup: false,
        rotatable: false,
      };
    }

    const askPool = this.usable([
      ...HelpSuggestedQuestionsComponent.ASK_FREE,
      ...HelpSuggestedQuestionsComponent.ASK_EMP,
    ]);
    const doPool = this.usable([
      ...HelpSuggestedQuestionsComponent.DO_FREE,
      ...HelpSuggestedQuestionsComponent.DO_EMP,
    ]);

    const ask = this.rotate(askPool, 2, seed).map((k, i) => this.text(k, seed + i));
    if (this.tourContextKey) {
      ask.push(this.text('TOUR_CONTEXT', 0));
    }
    const doItems = this.rotate(doPool, 3, seed).map((k, i) => this.text(k, seed + i));

    return { ask, do: doItems, setup: false, rotatable: askPool.length + doPool.length > 5 };
  }

  /**
   * C5 Schritt 2 — filtert Beispiele, deren Voraussetzung im Betrieb fehlt.
   *
   * Keine Abteilungsvergleiche bei einer Abteilung, keine Zeitfragen ohne Zeiterfassung.
   */
  private usable(keys: readonly string[]): string[] {
    const mehrereAbteilungen = this.departments().length > 1;
    const zeiterfassung = this.subscription.hasFeature(Feature.TIME_TRACKING_BASIC);
    return keys.filter((k) => {
      if (HelpSuggestedQuestionsComponent.NEEDS_DEPARTMENT.has(k) && !mehrereAbteilungen) {
        return false;
      }
      if (HelpSuggestedQuestionsComponent.NEEDS_TIME_TRACKING.has(k) && !zeiterfassung) {
        return false;
      }
      return true;
    });
  }

  /** Wählt `count` Beispiele ab Offset `seed` (rotierend, ohne Wiederholung solange Pool groß genug). */
  private rotate(pool: string[], count: number, seed: number): string[] {
    if (pool.length <= count) {
      return [...pool];
    }
    const out: string[] = [];
    for (let i = 0; i < count; i++) {
      out.push(pool[(seed + i) % pool.length]);
    }
    return out;
  }

  /** Uebersetzt einen Beispiel-Key und interpoliert einen ECHTEN Mitarbeiter-/Abteilungsnamen. */
  private text(key: string, index: number): string {
    const emps = this.employees();
    const deps = this.departments();
    const name = emps.length ? emps[((index % emps.length) + emps.length) % emps.length] : '';
    const dept = deps.length ? deps[((index % deps.length) + deps.length) % deps.length] : '';
    return this.translate.instant(`HELP_CHAT.SUGGESTED.${key}`, { name, dept });
  }

  /** Zeilentext je Art — mit Singular/Plural, weil „1 Schichten unbesetzt" falsch ist. */
  private pulseTextKey(kind: PulseKind, count: number): string {
    const suffix = count === 1 ? 'ONE' : 'MANY';
    return `HELP_CHAT.TODAY.${kind.toUpperCase()}_${suffix}`;
  }

  /**
   * Die Folgefrage je Art.
   *
   * ⚠️ Jeder dieser Sätze muss den Werkzeugkatalog treffen — sonst führt eine Zeile, die
   * „hier ist, was ansteht" verspricht, in die Hilfe-Rücklage. Die Zuordnung ist an den
   * `ExampleUtterances` ausgerichtet und mit `demo-assistent-vorschlaege-pruefen.py`
   * gegengeprüft.
   */
  private pulseQuestionKey(kind: PulseKind): string {
    switch (kind) {
      case 'openShift':
        return 'ASK_GAPS'; // shift_coverage
      case 'absenceRequest':
      case 'overtime':
      case 'changeRequest':
        return 'ASK_TO_APPROVE'; // get_action_items
      case 'shiftSwap':
        return 'ASK_SWAPS'; // search_shift_swaps
    }
  }
}
